import { apiClient } from './axios';

export interface WhatsAppTemplate {
  id: string;
  name: string;
  language: string;
  category: string;
  components: unknown;
  status: string;
  meta_template_id: string | null;
  rejected_reason: string | null;
  whatsapp_business_account_id: string;
  created_at: string;
  updated_at: string;
}

/** A WhatsApp Business Account this platform can send templates from. */
export interface WhatsAppTemplateAccount {
  waba_id: string;
  name: string | null;
  source: 'account' | 'setting' | 'number';
}

/** Per-account outcome of a create / replicate / delete call. */
export interface TemplateAccountResult {
  waba_id: string;
  status: 'created' | 'linked' | 'failed' | 'skipped' | 'deleted';
  meta_template_id?: string;
  error?: string;
}

export interface TemplateButton {
  type: 'URL' | 'QUICK_REPLY' | 'PHONE_NUMBER';
  text: string;
  url?: string;
  example?: string[];
}

export interface TemplateComponent {
  type: 'HEADER' | 'BODY' | 'FOOTER' | 'BUTTONS';
  format?: 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT';
  text?: string;
  buttons?: TemplateButton[];
  example?: {
    body_text?: string[][];
    header_text?: string[];
    header_url?: string[];
  };
}

export interface CarouselCard {
  components: TemplateComponent[];
}

export interface CarouselComponent {
  type: 'CAROUSEL';
  cards: CarouselCard[];
}

export type AnyComponent = TemplateComponent | CarouselComponent;

export interface CreateWhatsAppTemplateDto {
  name: string;
  language: string;
  category: 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';
  components: AnyComponent[];
  /** Accounts to create the template on. Omitted = every account. */
  waba_ids?: string[];
}

export type CreateWhatsAppTemplateResponse = WhatsAppTemplate & {
  accounts: TemplateAccountResult[];
};

export interface ReplicateWhatsAppTemplateResponse {
  accounts: TemplateAccountResult[];
}

export interface DeleteWhatsAppTemplateResponse {
  success: true;
  accounts: TemplateAccountResult[];
}

export interface SyncWhatsAppTemplatesResponse {
  count: number;
  synced_at: string;
  accounts: Array<{ waba_id: string; count: number }>;
}

export const whatsappTemplatesApi = {
  /** All templates, or only those the sender of `conversationId` can use. */
  list: (params?: { conversationId?: string }) =>
    apiClient.get<WhatsAppTemplate[]>('/whatsapp/templates', { params }).then((r) => r.data),

  /** Every WhatsApp Business Account templates can be managed on. */
  listAccounts: () =>
    apiClient.get<WhatsAppTemplateAccount[]>('/whatsapp/templates/accounts').then((r) => r.data),

  get: (id: string) =>
    apiClient.get<WhatsAppTemplate>(`/whatsapp/templates/${id}`).then((r) => r.data),

  create: (data: CreateWhatsAppTemplateDto) =>
    apiClient.post<CreateWhatsAppTemplateResponse>('/whatsapp/templates', data).then((r) => r.data),

  /** Create the template on the given accounts (omitted = every account missing it). */
  replicate: (id: string, wabaIds?: string[]) =>
    apiClient
      .post<ReplicateWhatsAppTemplateResponse>(`/whatsapp/templates/${id}/replicate`, {
        ...(wabaIds ? { waba_ids: wabaIds } : {}),
      })
      .then((r) => r.data),

  sync: () =>
    apiClient.post<SyncWhatsAppTemplatesResponse>('/whatsapp/templates/sync').then((r) => r.data),

  /** Delete from the given accounts (omitted = every account the template exists on). */
  delete: (id: string, wabaIds?: string[]) =>
    apiClient
      .delete<DeleteWhatsAppTemplateResponse>(`/whatsapp/templates/${id}`, {
        params: wabaIds?.length ? { wabaIds: wabaIds.join(',') } : undefined,
      })
      .then((r) => r.data),
};
